import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { auth } from '../firebase/firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import { functionsBaseUrl } from '../config/runtime';
import '../v2/workflows.css';

export default function VerifyPayment() {
    const location = useLocation();
    const navigate = useNavigate();
    const { currentUser, userLoading } = useAuth();
    const uid = currentUser?.uid;
    const params = new URLSearchParams(location.search);
    const rawReference = params.get('reference') || params.get('trxref') || localStorage.getItem('pendingReference');
    const reference = /^[A-Za-z0-9_-]{1,200}$/.test(rawReference || '') ? rawReference : null;
    const [retry, setRetry] = useState(0);
    const [state, setState] = useState({ status: 'checking', message: 'Confirming your renewal with Paystack…' });
    const signInPath = reference ? `/shop-login?returnTo=${encodeURIComponent(`/verify-payment?reference=${reference}`)}` : '/shop-login';

    useEffect(() => {
        let current = true, timer, requestTimer, controller;
        if (userLoading) return undefined;
        if (!reference || !uid) {
            setState({ status: 'error', message: !reference ? 'We could not find a payment reference. Return using the link from Paystack, or check your billing status in account settings.' : 'Sign in to the account that made this payment. We will return here to confirm the renewal.' });
            return undefined;
        }
        setState({ status: 'checking', message: 'Confirming your renewal with Paystack…' });
        const deadline = setTimeout(() => {
            current = false; clearTimeout(timer); clearTimeout(requestTimer); controller?.abort();
            setState({ status: 'delayed', message: 'Confirmation is taking longer than expected. Check again without making another payment. Existing work remains available in your workspace.' });
        }, 60000);
        const verify = async () => {
            controller = new AbortController();
            requestTimer = setTimeout(() => controller.abort(), 12000);
            try {
                if (!auth.currentUser || auth.currentUser.uid !== uid) throw new Error('Account changed');
                const token = await auth.currentUser.getIdToken();
                const response = await fetch(`${functionsBaseUrl}/verifyPaystackPayment`, { method: 'POST', signal: controller.signal, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ reference, uid }) });
                const result = await response.json(); clearTimeout(requestTimer);
                if (!current) return;
                if (response.ok && response.status !== 202 && result.success && result.access?.canPublishListing) {
                    clearTimeout(deadline);
                    localStorage.removeItem('pendingReference'); localStorage.removeItem('pendingUID');
                    setState({ status: 'success', message: 'Your renewal is verified and your listing is active. Taking you to billing settings…' });
                    timer = setTimeout(() => navigate('/shop/settings#billing', { replace: true }), 2500);
                } else if (response.status === 202 && result.pending) {
                    setState({ status: 'checking', message: 'Payment received. We’re waiting for the verified subscription period. Please don’t pay again.' });
                    timer = setTimeout(verify, 5000);
                } else {
                    clearTimeout(deadline);
                    setState({ status: 'error', message: result.error || 'This payment has not restored current listing access. Check billing settings or contact support with this reference. Please don’t pay again.' });
                }
            } catch {
                clearTimeout(requestTimer); if (!current) return; clearTimeout(deadline);
                setState({ status: 'delayed', message: 'We couldn’t reach the payment verifier. Check again when your connection is restored. Please don’t make another payment.' });
            }
        };
        verify();
        return () => { current = false; clearTimeout(timer); clearTimeout(requestTimer); clearTimeout(deadline); controller?.abort(); };
    }, [reference, uid, userLoading, retry, navigate]);

    return <main className="v2-auth-shell v2-auth-main">
        <Helmet><title>Confirm renewal - Signet Print</title></Helmet>
        <section className="v2-auth-card v2-auth-form">
            <p className="v2-flow-kicker">Signet Print</p>
            <div role="status" aria-live="polite" aria-atomic="true">
                {(state.status === 'checking' || userLoading) && <span className="v2-payment-spinner" aria-hidden="true" />}
                <h1>{state.status === 'success' ? 'Renewal confirmed' : state.status === 'checking' || userLoading ? 'Confirming renewal' : state.status === 'delayed' ? 'Still waiting for confirmation' : 'Renewal needs attention'}</h1>
                <p>{state.message}</p>
            </div>
            {reference && <p className="v2-payment-reference">Payment reference: <strong>{reference}</strong></p>}
            {!userLoading && !uid && <Link className="v2-button v2-button-fill" to={signInPath}>Sign in to confirm payment</Link>}
            {uid && reference && ['delayed', 'error'].includes(state.status) && <button className="v2-button v2-button-fill" onClick={() => setRetry(value => value + 1)}>Check payment again</button>}
            {uid && <Link className="v2-button" to="/shop/settings#billing">Return to billing settings</Link>}
            {['delayed', 'error'].includes(state.status) && <Link className="v2-button" to="/contact">Contact support</Link>}
        </section>
    </main>;
}
